import React, { useEffect, useState } from "react";
import { AlertTriangle, CalendarDays, Clock, Copy, ExternalLink, MessageCircle, Share2, X } from "lucide-react";
import { getScheduleAttachmentBlob, getScheduleShareKey } from "../api";
import showToast from "../utils/toast";
import { ClassTimings, NativePickerField, ampm, dayMonth, escapeHtml, fitsClassTimings, fromKey, hhmm, longDate } from "./dailyScheduleShared";

// Share / Reschedule dialogs opened from a visit card's ⋮ menu on the admin Daily Schedule.
// View and Edit open the lesson plan page instead (AdminLessonPlanSchedule.jsx).

export const STATUS_LABEL = { active: "Scheduled", completed: "Completed", cancelled: "Cancelled" };

const timeRange = (s) => `${ampm(hhmm(s.startTime))} – ${ampm(hhmm(s.endTime))}`;
const planRows = (s) => (Array.isArray(s?.lessonPlan?.children) ? s.lessonPlan.children : []);
// Same wording as the plan text the Create Lesson Plan page saves in the visit description.
const planLines = (rows) => rows.map((r, i) => `${i + 1}. ${r.childName} — Computer: ${r.computer || "-"}; One-on-One: ${r.oneOnOne || "-"}${r.note ? `; Note: ${r.note}` : ""}`);

// Visits saved without `lessonPlan` (e.g. before the schedule API kept it) have the plan only as that
// description text. Read it back into rows so it shows in the same table; [] when it isn't plan text.
const PLAN_LINE = /^\d+\.\s+(.+?)\s+—\s+Computer:\s*(.*?);\s*One-on-One:\s*(.*?)(?:;\s*Note:\s*(.*))?$/;
const orBlank = (value) => (value === "-" ? "" : value);
export const planRowsFromText = (text) => {
  const matches = String(text || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean).map((line) => line.match(PLAN_LINE));
  if (!matches.length || matches.some((m) => !m)) return [];
  return matches.map(([, childName, computer, oneOnOne, note]) => ({ childName, computer: orBlank(computer), oneOnOne: orBlank(oneOnOne), note: note || "" }));
};

// The opening lines of every shared visit message: "Sanstha : Mohor Girls", "Time : 03:30 PM – 06:30 PM",
// "ISF Faculty : <coaches>", "Date : 23 Oct 2026", "Day : Friday".
const visitHeadLines = ({ visit, centreName, coachNames }) => {
  const day = fromKey(visit.date);
  return [
    `Sanstha : ${centreName}`,
    `Time : ${timeRange(visit)}`,
    `ISF Faculty : ${coachNames}`,
    `Date : ${dayMonth(day)} ${day.getFullYear()}`,
    `Day : ${day.toLocaleDateString("en-GB", { weekday: "long" })}`,
  ];
};

export const visitShareText = ({ visit, centreName, coachNames }) => {
  const first = (field) => visit.members.map((m) => String(m[field] || "").trim()).find(Boolean) || "";
  // Coaches can edit their copy of the child-wise plan, so it is newer than the description text.
  const withPlan = visit.members.find((m) => planRows(m).length);
  const plan = withPlan ? planLines(planRows(withPlan)).join("\n") : first("description");
  return [
    ...visitHeadLines({ visit, centreName, coachNames }),
    `Status: ${STATUS_LABEL[visit.status] || visit.status}`,
    first("learningsFocus") && `Learning focus: ${first("learningsFocus")}`,
    first("specialInstructions") && `Instructions: ${first("specialInstructions")}`,
    first("healthCheck") && `Health check: ${first("healthCheck")}`,
    plan && `\nLesson plan:\n${plan}`,
  ].filter(Boolean).join("\n");
};

// Also used by the centre table's edit dialogs (DailyScheduleCentreModals.jsx).
export function VisitDialog({ titleId, title, className = "", onClose, onSubmit, footer, children }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const Card = onSubmit ? "form" : "div";
  return (
    <div className="ds-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <Card className={`ds-modal__card ${className}`} role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={onSubmit}>
        <div className="ds-modal__head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" aria-label="Close" onClick={onClose}><X size={20} /></button>
        </div>
        {children}
        <div className="ds-modal__foot">{footer}</div>
      </Card>
    </div>
  );
}

// The attachment route needs the auth header, so fetch the file and open it as a blob URL.
export const openScheduleAttachment = async (scheduleId, item) => {
  const tab = window.open("", "_blank"); // opened synchronously so it isn't popup-blocked
  try {
    const blob = await getScheduleAttachmentBlob(scheduleId, item._id);
    const url = URL.createObjectURL(new Blob([blob], { type: item.mimeType || blob.type }));
    if (tab) tab.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    if (tab) tab.close();
    showToast("Unable to open the attachment", "error");
  }
};

// Opens the visit read-only without a login (pages/SharedSchedulePage.jsx). The Balgruha name is
// there for people reading the link; the page loads the visit by the key, which is only valid for
// that Balgruha and date.
// Links point at this site unless REACT_APP_SHARE_BASE_URL names another (public) address for them.
const shareOrigin = () => String(process.env.REACT_APP_SHARE_BASE_URL || window.location.origin).replace(/\/+$/, "");
// "*" is WhatsApp's bold marker, so it is escaped too; everything else URLSearchParams already escapes.
export const visitShareUrl = ({ date, centreName, key, origin = shareOrigin() }) =>
  `${origin}/daily-schedule/shared?${String(new URLSearchParams({ date, balagruha: centreName, key })).replace(/\*/g, "%2A")}`;

// WhatsApp and other apps only turn public web addresses into links; a localhost link arrives as plain text.
export const isLocalLink = (url) => {
  try {
    return /^(localhost|127(\.\d+){3}|0\.0\.0\.0|\[::1\])$/i.test(new URL(url).hostname);
  } catch {
    return false;
  }
};

const visitLinkIntro = ({ visit, centreName, coachNames }) => [
  ...visitHeadLines({ visit, centreName, coachNames }),
  "View the schedule:",
].join("\n");

// The URL sits alone on its last line, with nothing touching it, so apps detect it as a link.
export const visitLinkText = ({ visit, centreName, coachNames, url }) => `${visitLinkIntro({ visit, centreName, coachNames })}\n${url}`;

// Copied next to the plain text so that pasting into mail, Docs, Teams etc. gives a clickable link.
const visitLinkHtml = ({ visit, centreName, coachNames, url }) =>
  `${escapeHtml(visitLinkIntro({ visit, centreName, coachNames })).replace(/\n/g, "<br>")}<br><a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`;

// Shares a link to the visit; if the link can't be made, the visit details go out as text instead.
export function ShareVisitModal({ visit, centreName, coachNames = "", fallbackText, onClose }) {
  const [link, setLink] = useState({ status: "loading", url: "" });
  useEffect(() => {
    let cancelled = false;
    setLink({ status: "loading", url: "" });
    getScheduleShareKey(visit.centreId, visit.date)
      .then((key) => {
        if (!key) throw new Error("No share key");
        if (!cancelled) setLink({ status: "ready", url: visitShareUrl({ date: visit.date, centreName, key }) });
      })
      .catch(() => { if (!cancelled) setLink({ status: "error", url: "" }); });
    return () => { cancelled = true; };
  }, [visit.centreId, visit.date, centreName]);

  const loading = link.status === "loading";
  const text = link.status === "ready" ? visitLinkText({ visit, centreName, coachNames, url: link.url }) : link.status === "error" ? fallbackText : "";
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  // With a link, copy it as plain text and as a hyperlink; apps take whichever they can paste.
  const writeLink = async () => {
    if (!link.url || typeof ClipboardItem !== "function" || typeof navigator.clipboard?.write !== "function") return false;
    try {
      const html = visitLinkHtml({ visit, centreName, coachNames, url: link.url });
      await navigator.clipboard.write([new ClipboardItem({
        "text/plain": new Blob([text], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      })]);
      return true;
    } catch {
      return false; // e.g. rich clipboard blocked; plain text below still has the link
    }
  };
  const copy = async () => {
    try {
      if (!(await writeLink())) await navigator.clipboard.writeText(text);
      showToast(link.url ? "Visit link copied" : "Visit details copied", "success");
    } catch {
      showToast("Could not copy. Select the text and copy it instead.", "error");
    }
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  // `url` makes the share sheet hand the app a link, not just a message.
  const shareSheet = async () => {
    const data = link.url ? { title: "Visit", text: visitLinkIntro({ visit, centreName, coachNames }), url: link.url } : { title: "Visit", text };
    try { await navigator.share(data); } catch { /* share sheet dismissed */ }
  };

  return (
    <VisitDialog
      titleId="ds-share-title"
      title="Share Visit"
      className="ds-share"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose}>Close</button>
          {canShare && <button type="button" className="ds-btn ds-btn--ghost" onClick={shareSheet} disabled={loading}><Share2 size={15} />More</button>}
          <button type="button" className="ds-btn ds-btn--ghost" onClick={copy} disabled={loading}><Copy size={15} />Copy text</button>
          <button type="button" className="ds-btn ds-btn--whatsapp" onClick={whatsapp} disabled={loading}><MessageCircle size={15} />WhatsApp</button>
        </>
      )}
    >
      <textarea
        className={`ds-share__text${link.url ? " ds-share__text--link" : ""}`}
        readOnly
        value={text}
        placeholder={loading ? "Creating link…" : ""}
        rows={link.url ? 4 : 10}
        aria-label="Visit details to share"
        onFocus={(e) => e.target.select()}
      />
      {link.url && (
        <p className="ds-share__link">
          <ExternalLink size={14} aria-hidden="true" />
          <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
        </p>
      )}
      {link.url && isLocalLink(link.url) && (
        <p className="ds-share__note" role="note">
          <AlertTriangle size={14} />
          This link starts with localhost, so it only opens on this computer and WhatsApp shows it as plain text. Links shared from the live site are clickable.
        </p>
      )}
      {link.status === "error" && (
        <p className="ds-share__note" role="status"><AlertTriangle size={14} />Could not create a link, so the visit details will be shared as text.</p>
      )}
    </VisitDialog>
  );
}

export function RescheduleVisitModal({ visit, centreName, coachNames, timings, saving, onClose, onSave }) {
  const [form, setForm] = useState(() => ({ date: visit.date, startTime: hhmm(visit.startTime), endTime: hhmm(visit.endTime) }));
  const cancelled = visit.status === "cancelled";
  const fits = fitsClassTimings(timings, form.date, form.startTime, form.endTime);
  const dateLabel = form.date ? `${dayMonth(fromKey(form.date))} ${fromKey(form.date).getFullYear()}` : "Select date";

  return (
    <VisitDialog
      titleId="ds-reschedule-title"
      title="Reschedule Visit"
      className="ds-reschedule"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
      footer={(
        <>
          <button type="button" className="ds-btn ds-btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="ds-btn ds-btn--primary" disabled={saving}>{saving ? "Saving…" : "Reschedule"}</button>
        </>
      )}
    >
      <p className="ds-reschedule__current">
        <b>{centreName}</b>
        <span>{coachNames}</span>
        <small>{cancelled ? "Cancelled visit:" : "Currently"} {longDate(visit.date)}, {timeRange(visit)}</small>
      </p>
      {cancelled && (
        <p className="ds-reschedule__restore" role="note">
          Rescheduling brings this cancelled visit back for all its coaches. Keep the date and time to continue it as planned, or pick new ones.
        </p>
      )}
      <div className="ds-modal__times ds-reschedule__fields">
        <div className="ds-field">
          <span>New Date <em>*</em></span>
          <NativePickerField type="date" icon={<CalendarDays size={14} />} value={form.date} display={dateLabel} onChange={(date) => setForm({ ...form, date })} />
        </div>
        <div className="ds-field">
          <span>Start Time <em>*</em></span>
          <NativePickerField type="time" icon={<Clock size={14} />} value={form.startTime} display={ampm(form.startTime)} onChange={(startTime) => setForm({ ...form, startTime })} />
        </div>
        <div className="ds-field">
          <span>End Time <em>*</em></span>
          <NativePickerField type="time" icon={<Clock size={14} />} value={form.endTime} display={ampm(form.endTime)} onChange={(endTime) => setForm({ ...form, endTime })} />
        </div>
      </div>
      <p className="ds-reschedule__hint"><b>Available:</b> <ClassTimings timings={timings} /></p>
      {fits === false && (
        <p className="ds-reschedule__warn" role="status"><AlertTriangle size={14} />This is outside {centreName}&apos;s available class timings.</p>
      )}
    </VisitDialog>
  );
}
